import type { IconName } from './icons';
import type {
  AdminCategoryDTO,
  AdminUserDTO,
  AdminCategoryDetailDTO,
  AdminDashboardStatsDTO,
  AdminMaterialDTO,
  AdminMaterialsResponse,
  AdminPaymentDetailDTO,
  AdminPaymentsResponse,
  AdminProductBody,
  AdminProductDTO,
  AdminSettingsDTO,
  PlaybackDTO,
  ReferralSettingsDTO,
  ReferralMeDTO,
  ReferralEarningsResponse,
  AdminReferrersResponse,
  AdminPendingPaymentDTO,
  AdminReferralDetailDTO,
  AdminEventDTO,
  AdminEventBody,
  EventStatsDTO,
  EventListItemDTO,
  EventDetailDTO,
  EventRecordingProgress,
  AdminUsersResponse,
  ActivityMembersResponse,
  ActivityStatsDTO,
  ActivityWeekdayResponse,
  RevenueDetailDTO,
  DashboardMembersResponse,
  DashboardMetric,
  DashboardPeriod,
  PaymentStatus,
  PopularMaterialsResponse,
  AdminCommentDTO,
  AdminCommentsResponse,
  AdminCommentStatsDTO,
  AdminCommentStatus,
  Block,
  CategoryDTO,
  CommentDTO,
  CommentListResponse,
  CommentReportReason,
  CategoryDetailDTO,
  FavoriteItemDTO,
  MaterialChapter,
  MaterialDetailDTO,
  MaterialType,
  PlaybackStateDTO,
  ProgressByCategoryItemDTO,
  RecentItemDTO,
  SessionDTO,
  SubscriptionStatus,
  UtmDimension,
  UtmReportDTO,
  UtmSelection,
} from './types';
import { UTM_DIMENSIONS } from './types';
import type { PlayerSettings } from './player/settings';

import { get } from 'svelte/store';
import { adminMode } from './admin-mode';
import { getStartParam } from './telegram';

const BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

// Session token store. The cookie is the primary mechanism (iOS/desktop), but
// the Telegram Android WebView drops our SameSite=None cookie, so we also keep
// the JWT and replay it via the Authorization header.
//
// Primary store is an in-memory variable: the Telegram Android WebView can also
// block/partition localStorage, in which case the cookie AND a localStorage
// token would both be unavailable. The in-memory copy always survives within a
// page session (which is all we need: login → store → playback). localStorage
// is a best-effort backstop so the token also survives a reload.
const TOKEN_KEY = 'fy_session_token';
let memToken: string | null = null;
export function getStoredToken(): string | null {
  if (memToken) return memToken;
  try {
    memToken = localStorage.getItem(TOKEN_KEY);
  } catch {
    memToken = null;
  }
  return memToken;
}
export function setStoredToken(token: string | null): void {
  memToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode / storage disabled — in-memory copy still works this session */
  }
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Forward adminMode to the server so user-facing endpoints can lift the
  // isPublished/isActive filters for admins previewing drafts. The server
  // ignores the header for non-admin users, so a regular subscriber can't
  // spoof it into seeing drafts.
  const mode = get(adminMode);
  const token = getStoredToken();
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(mode === 'admin' ? { 'X-Admin-Mode': 'admin' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let code = 'unknown';
    try {
      const body = (await res.json()) as { error?: string };
      code = body.error ?? `http_${res.status}`;
    } catch {
      code = `http_${res.status}`;
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

export const api = {
  authMe: async (initData: string): Promise<SessionDTO> => {
    // start_param їде разом з логіном заради реферальних посилань
    // (`?startapp=ref_<код>`): прив'язка реферера — серверна справа, і клієнту
    // її не видно. Читаємо тут, а не в кожній сторінці, бо authMe викликається
    // з десятка місць, і перший виклик після відкриття може бути будь-яким із
    // них. Повторні виклики нешкідливі: прив'язка одноразова за побудовою.
    const startParam = getStartParam();
    const dto = await request<SessionDTO>('/auth/me', {
      method: 'POST',
      body: JSON.stringify({ initData, ...(startParam ? { startParam } : {}) }),
    });
    if (dto.token) setStoredToken(dto.token);
    return dto;
  },
  authSession: async (): Promise<SessionDTO> => {
    const dto = await request<SessionDTO>('/auth/session', { method: 'GET' });
    if (dto.token) setStoredToken(dto.token);
    return dto;
  },
  authLogout: async (): Promise<{ ok: true }> => {
    try {
      return await request<{ ok: true }>('/auth/logout', { method: 'POST' });
    } finally {
      setStoredToken(null);
    }
  },
  // Dev-only: exists only when API was started with ENABLE_DEV_LOGIN=true AND a
  // DEV_LOGIN_SECRET. The secret is sent via the x-dev-login-secret header;
  // calling on prod returns 404/403 — safe to keep in the bundle.
  authDevLogin: async (telegramId: string): Promise<SessionDTO> => {
    const devSecret = import.meta.env.VITE_DEV_LOGIN_SECRET as string | undefined;
    const dto = await request<SessionDTO>('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ telegramId }),
      headers: devSecret ? { 'x-dev-login-secret': devSecret } : {},
    });
    if (dto.token) setStoredToken(dto.token);
    return dto;
  },
  categories: (): Promise<{ items: CategoryDTO[] }> => request('/categories', { method: 'GET' }),
  category: (id: string): Promise<CategoryDetailDTO> =>
    request(`/categories/${id}`, { method: 'GET' }),
  material: (id: string): Promise<MaterialDetailDTO> =>
    request(`/materials/${id}`, { method: 'GET' }),
  materialPlayback: (id: string): Promise<PlaybackDTO> =>
    request(`/materials/${id}/playback`, { method: 'GET' }),
  toggleFavorite: (materialId: string): Promise<{ isFavorite: boolean }> =>
    request(`/materials/${materialId}/favorite`, { method: 'POST' }),
  myFavorites: (): Promise<{ items: FavoriteItemDTO[] }> =>
    request('/users/me/favorites', { method: 'GET' }),
  materialComments: (
    materialId: string,
    opts: { page?: number; limit?: number } = {},
  ): Promise<CommentListResponse> => {
    const params = new URLSearchParams();
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/materials/${materialId}/comments${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  createComment: (body: {
    materialId: string;
    text: string;
    parentId?: string | null;
  }): Promise<CommentDTO> =>
    request('/comments', { method: 'POST', body: JSON.stringify(body) }),
  deleteComment: (commentId: string): Promise<{ ok: true }> =>
    request(`/comments/${commentId}`, { method: 'DELETE' }),
  toggleCommentLike: (commentId: string): Promise<{ liked: boolean; likeCount: number }> =>
    request(`/comments/${commentId}/like`, { method: 'POST' }),
  reportComment: (
    commentId: string,
    body: { reason: CommentReportReason },
  ): Promise<{ reported: true; alreadyReported: boolean }> =>
    request(`/comments/${commentId}/report`, { method: 'POST', body: JSON.stringify(body) }),
  playbackState: (materialId: string): Promise<PlaybackStateDTO> =>
    request(`/users/me/playback/${materialId}`, { method: 'GET' }),
  postProgress: (
    materialId: string,
    body: { sec: number; duration: number },
  ): Promise<{ ok: true; maxProgressPct: number; completedAt: string | null }> =>
    request(`/users/me/playback/${materialId}/progress`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  myRecent: (limit = 10): Promise<{ items: RecentItemDTO[] }> =>
    request(`/users/me/recent?limit=${limit}`, { method: 'GET' }),
  myProgressByCategory: (): Promise<{ items: ProgressByCategoryItemDTO[] }> =>
    request('/users/me/progress-by-category', { method: 'GET' }),
  adminUsers: (opts: {
    search?: string;
    statuses?: SubscriptionStatus[];
    tariffs?: string[];
    startOp?: 'before' | 'after' | 'on' | 'between';
    startDate1?: string;
    startDate2?: string;
    endOp?: 'before' | 'after' | 'on' | 'between';
    endDate1?: string;
    endDate2?: string;
    emailOp?: 'empty' | 'notEmpty' | 'contains' | 'notContains' | 'equals' | 'notEquals';
    emailValue?: string;
    phoneOp?: 'empty' | 'notEmpty' | 'contains' | 'notContains' | 'equals' | 'notEquals';
    phoneValue?: string;
    usernameOp?: 'empty' | 'notEmpty' | 'contains' | 'notContains' | 'equals' | 'notEquals';
    usernameValue?: string;
    /** First-touch UTM контакту; `__none__` = «без мітки». */
    utm?: UtmSelection;
    page?: number;
    limit?: number;
  } = {}): Promise<AdminUsersResponse> => {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.statuses) for (const s of opts.statuses) params.append('status', s);
    if (opts.tariffs) for (const t of opts.tariffs) params.append('tariff', t);
    if (opts.startOp) params.set('startOp', opts.startOp);
    if (opts.startDate1) params.set('startDate1', opts.startDate1);
    if (opts.startDate2) params.set('startDate2', opts.startDate2);
    if (opts.endOp) params.set('endOp', opts.endOp);
    if (opts.endDate1) params.set('endDate1', opts.endDate1);
    if (opts.endDate2) params.set('endDate2', opts.endDate2);
    for (const dim of UTM_DIMENSIONS) {
      const vals = opts.utm?.[dim];
      if (vals && vals.length) params.set(dim, vals.join(','));
    }
    if (opts.emailOp) params.set('emailOp', opts.emailOp);
    if (opts.emailValue) params.set('emailValue', opts.emailValue);
    if (opts.phoneOp) params.set('phoneOp', opts.phoneOp);
    if (opts.phoneValue) params.set('phoneValue', opts.phoneValue);
    if (opts.usernameOp) params.set('usernameOp', opts.usernameOp);
    if (opts.usernameValue) params.set('usernameValue', opts.usernameValue);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/admin/users${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  adminUser: (telegramId: string): Promise<AdminUserDTO> =>
    request(`/admin/users/${telegramId}`, { method: 'GET' }),
  adminDashboardStats: (
    period: DashboardPeriod = 'month',
    range?: { from?: string; to?: string },
    productId?: string,
  ): Promise<AdminDashboardStatsDTO> => {
    const params = new URLSearchParams({ period, tz: String(new Date().getTimezoneOffset()) });
    if (period === 'custom') {
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
    }
    if (productId) params.set('productId', productId);
    return request(`/admin/dashboard/stats?${params.toString()}`, { method: 'GET' });
  },
  adminDashboardMembers: (
    metric: DashboardMetric,
    period: DashboardPeriod = 'month',
    range?: { from?: string; to?: string },
    productId?: string,
    page = 1,
  ): Promise<DashboardMembersResponse> => {
    const params = new URLSearchParams({
      period,
      metric,
      page: String(page),
      tz: String(new Date().getTimezoneOffset()),
    });
    if (period === 'custom') {
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
    }
    if (productId) params.set('productId', productId);
    return request(`/admin/dashboard/members?${params.toString()}`, { method: 'GET' });
  },
  adminDashboardPayments: (
    status: 'succeeded' | 'refunded',
    period: DashboardPeriod = 'month',
    range?: { from?: string; to?: string },
    productId?: string,
    page = 1,
    opts?: {
      tariffId?: string;
      source?: string;
      /** Drill-in зі звіту по мітках: один вимір + одне значення. */
      utmDim?: UtmDimension;
      utmValue?: string;
    },
  ): Promise<AdminPaymentsResponse> => {
    const params = new URLSearchParams({
      period,
      status,
      page: String(page),
      tz: String(new Date().getTimezoneOffset()),
    });
    if (period === 'custom') {
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
    }
    if (productId) params.set('productId', productId);
    if (opts?.tariffId) params.set('tariffId', opts.tariffId);
    if (opts?.source) params.set('source', opts.source);
    if (opts?.utmDim && opts.utmValue) {
      params.set('utmDim', opts.utmDim);
      params.set('utmValue', opts.utmValue);
    }
    return request(`/admin/dashboard/payments?${params.toString()}`, { method: 'GET' });
  },
  adminPopularMaterials: (page = 1): Promise<PopularMaterialsResponse> =>
    request(`/admin/dashboard/popular-materials?page=${page}`, { method: 'GET' }),
  adminDashboardActivity: (date: string, page = 1): Promise<ActivityMembersResponse> =>
    request(`/admin/dashboard/activity?date=${encodeURIComponent(date)}&page=${page}`, { method: 'GET' }),
  adminActivityStats: (month: string): Promise<ActivityStatsDTO> => {
    const tz = String(new Date().getTimezoneOffset());
    return request(`/admin/dashboard/activity-stats?month=${encodeURIComponent(month)}&tz=${tz}`, {
      method: 'GET',
    });
  },
  adminActivityDay: (date: string, page = 1): Promise<ActivityMembersResponse> => {
    const tz = String(new Date().getTimezoneOffset());
    return request(
      `/admin/dashboard/activity-day?date=${encodeURIComponent(date)}&tz=${tz}&page=${page}`,
      { method: 'GET' },
    );
  },
  adminActivityWeekday: (): Promise<ActivityWeekdayResponse> => {
    const tz = String(new Date().getTimezoneOffset());
    return request(`/admin/dashboard/activity-weekday?tz=${tz}`, { method: 'GET' });
  },
  adminRevenueDetail: (
    period: DashboardPeriod = 'month',
    range?: { from?: string; to?: string },
    productId?: string,
  ): Promise<RevenueDetailDTO> => {
    const params = new URLSearchParams({ period, tz: String(new Date().getTimezoneOffset()) });
    if (period === 'custom') {
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
    }
    if (productId) params.set('productId', productId);
    return request(`/admin/dashboard/revenue-detail?${params.toString()}`, { method: 'GET' });
  },
  adminPayments: (opts: {
    search?: string;
    status?: PaymentStatus[];
    productId?: string;
    unmatchedOnly?: boolean;
    source?: string[];
    /** UTM-мітки: по одному масиву значень на вимір; `__none__` = «без мітки». */
    utm?: UtmSelection;
    dateOp?: 'before' | 'after' | 'on' | 'between';
    date1?: string;
    date2?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<AdminPaymentsResponse> => {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.status && opts.status.length) params.set('status', opts.status.join(','));
    if (opts.productId) params.set('productId', opts.productId);
    if (opts.unmatchedOnly) params.set('unmatchedOnly', 'true');
    if (opts.source && opts.source.length) params.set('source', opts.source.join(','));
    for (const dim of UTM_DIMENSIONS) {
      const vals = opts.utm?.[dim];
      if (vals && vals.length) params.set(dim, vals.join(','));
    }
    if (opts.dateOp) params.set('dateOp', opts.dateOp);
    if (opts.date1) params.set('date1', opts.date1);
    if (opts.date2) params.set('date2', opts.date2);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/admin/payments${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  adminPaymentSources: (): Promise<{ items: { source: string; count: number }[]; hasNone: boolean }> =>
    request('/admin/payments/sources', { method: 'GET' }),
  /**
   * Наявні значення одного виміру фільтра з лічильниками.
   * `scope='contacts'` читає first-touch контактів замість міток оплат.
   * `q` + `limit` потрібні для utm_content/utm_term, де значень сотні.
   */
  adminFilterValues: (opts: {
    field: 'source' | UtmDimension;
    scope?: 'payments' | 'contacts';
    q?: string;
    limit?: number;
  }): Promise<{ items: { value: string; count: number }[]; hasNone: boolean }> => {
    const params = new URLSearchParams({ field: opts.field });
    if (opts.scope) params.set('scope', opts.scope);
    if (opts.q) params.set('q', opts.q);
    if (opts.limit) params.set('limit', String(opts.limit));
    return request(`/admin/payments/filter-values?${params.toString()}`, { method: 'GET' });
  },
  adminUtmReport: (
    dim: UtmReportDTO['dim'],
    period: DashboardPeriod = 'month',
    range?: { from?: string; to?: string },
    productId?: string,
  ): Promise<UtmReportDTO> => {
    const params = new URLSearchParams({
      dim,
      period,
      tz: String(new Date().getTimezoneOffset()),
    });
    if (period === 'custom') {
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
    }
    if (productId) params.set('productId', productId);
    return request(`/admin/dashboard/utm?${params.toString()}`, { method: 'GET' });
  },
  adminPayment: (id: string): Promise<AdminPaymentDetailDTO> =>
    request(`/admin/payments/${id}`, { method: 'GET' }),
  adminUnmatchedPayments: (
    productId?: string,
  ): Promise<{ items: { rawProduct: string; count: number }[] }> =>
    request(`/admin/payments/unmatched-products${productId ? `?productId=${productId}` : ''}`, {
      method: 'GET',
    }),
  adminBackfillContacts: (): Promise<{ ok: true; started?: boolean; alreadyRunning?: boolean }> =>
    request('/admin/contacts/backfill', { method: 'POST' }),
  adminBackfillContactsStatus: (): Promise<{ running: boolean }> =>
    request('/admin/contacts/backfill', { method: 'GET' }),
  adminCreatePayment: (body: {
    telegramId?: string;
    product?: string;
    amountUah?: number;
    currency?: string;
    status: PaymentStatus;
    occurredAt: string;
    orderRef?: string;
    email?: string;
    phone?: string;
    name?: string;
    paymentMethod?: string;
  }): Promise<{ id: string; duplicate: boolean }> =>
    request('/admin/payments', { method: 'POST', body: JSON.stringify(body) }),
  adminTariffs: (): Promise<{ items: string[] }> =>
    request('/admin/tariffs', { method: 'GET' }),
  adminCategories: (): Promise<{ items: AdminCategoryDTO[] }> =>
    request('/admin/categories', { method: 'GET' }),
  adminMaterials: (opts: {
    search?: string;
    type?: MaterialType;
    categoryIds?: string[];
    publish?: 'published' | 'draft';
    page?: number;
    limit?: number;
  } = {}): Promise<AdminMaterialsResponse> => {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.type) params.set('type', opts.type);
    if (opts.categoryIds) {
      for (const id of opts.categoryIds) params.append('categoryId', id);
    }
    if (opts.publish) params.set('publish', opts.publish);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/admin/materials${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  adminMaterial: (id: string): Promise<AdminMaterialDTO> =>
    request(`/admin/materials/${id}`, { method: 'GET' }),
  adminMaterialPatch: (
    id: string,
    body: {
      isPublished?: boolean;
      title?: string;
      description?: string | null;
      categoryId?: string | null;
      body?: Block[] | null;
      chapters?: MaterialChapter[] | null;
    },
  ): Promise<AdminMaterialDTO> =>
    request(`/admin/materials/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteMaterial: (id: string): Promise<{ ok: true }> =>
    request(`/admin/materials/${id}`, { method: 'DELETE' }),
  adminComments: (
    opts: {
      search?: string;
      materialId?: string;
      status?: AdminCommentStatus;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<AdminCommentsResponse> => {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.materialId) params.set('materialId', opts.materialId);
    if (opts.status) params.set('status', opts.status);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request(`/admin/comments${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  /** Два лічильники для бейджа «є нерозглянуті скарги» — дешевше за список. */
  adminCommentStats: (): Promise<AdminCommentStatsDTO> =>
    request('/admin/comments/stats', { method: 'GET' }),
  /** «Розглянув, коментар лишається»: гасить бейдж, історію скарг зберігає. */
  adminDismissCommentReports: (id: string): Promise<{ ok: true }> =>
    request(`/admin/comments/${id}/dismiss`, { method: 'POST' }),
  /** Відповідь команди зі списку; на відповідь — чіпляється до кореня гілки. */
  adminReplyComment: (id: string, text: string): Promise<AdminCommentDTO> =>
    request(`/admin/comments/${id}/reply`, { method: 'POST', body: JSON.stringify({ text }) }),
  /** Trigger Bunny's AI chapter generation. Async on Bunny's side — caller
   *  should poll bunnyChapterStatus to know when the chapters arrive. */
  adminGenerateChapters: (id: string, language = 'uk'): Promise<{ ok: true; queued: true }> =>
    request(`/admin/materials/${id}/generate-chapters`, {
      method: 'POST',
      body: JSON.stringify({ language }),
    }),
  /** Bunny billing balance + transcription rate — used to show cost / balance
   *  on the AI chapter button. balance is null when BUNNY_ACCOUNT_API_KEY
   *  isn't configured on the server. */
  adminBunnyAccountInfo: (): Promise<{
    balance: number | null;
    currency: string;
    transcribeUsdPerMinute: number;
  }> => request('/admin/bunny/account-info', { method: 'GET' }),
  /** Poll for the current Bunny state of a material's chapters during AI
   *  generation. Returns the chapters Bunny has right now (may be empty
   *  while still processing) plus the transcription/smart-generate status. */
  adminBunnyChapterStatus: (
    id: string,
  ): Promise<{
    chapters: MaterialChapter[];
    transcribingStatus: number | null;
    smartGenerateStatus: number | null;
  }> => request(`/admin/materials/${id}/bunny-chapter-status`, { method: 'GET' }),
  adminCategory: (id: string): Promise<AdminCategoryDetailDTO> =>
    request(`/admin/categories/${id}`, { method: 'GET' }),
  adminPatchCategory: (
    id: string,
    body: {
      icon?: IconName;
      name?: string;
      description?: string | null;
      isActive?: boolean;
      sortOrder?: number;
    },
  ): Promise<{ id: string }> =>
    request(`/admin/categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteCategory: (id: string): Promise<{ ok: true }> =>
    request(`/admin/categories/${id}`, { method: 'DELETE' }),
  adminReorderCategories: (ids: string[]): Promise<{ ok: true; count: number }> =>
    request('/admin/categories/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  adminReorderMaterials: (ids: string[]): Promise<{ ok: true; count: number }> =>
    request('/admin/materials/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  adminMoveCategoryMaterials: (
    id: string,
    targetCategoryId: string,
  ): Promise<{ ok: true; moved: number }> =>
    request(`/admin/categories/${id}/move-materials`, {
      method: 'POST',
      body: JSON.stringify({ targetCategoryId }),
    }),
  adminCreateCategory: (body: {
    icon: IconName;
    name: string;
    description?: string | null;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<{ id: string }> =>
    request('/admin/categories', { method: 'POST', body: JSON.stringify(body) }),
  adminProducts: (opts: {
    q?: string;
    scope?: 'all' | 'selected';
  } = {}): Promise<{ items: AdminProductDTO[] }> => {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.scope) params.set('scope', opts.scope);
    const qs = params.toString();
    return request(`/admin/products${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  adminProduct: (id: string): Promise<AdminProductDTO> =>
    request(`/admin/products/${id}`, { method: 'GET' }),
  adminCreateProduct: (body: AdminProductBody): Promise<AdminProductDTO> =>
    request('/admin/products', { method: 'POST', body: JSON.stringify(body) }),
  adminPatchProduct: (id: string, body: AdminProductBody): Promise<AdminProductDTO> =>
    request(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteProduct: (id: string): Promise<{ ok: true }> =>
    request(`/admin/products/${id}`, { method: 'DELETE' }),
  adminReorderProducts: (ids: string[]): Promise<{ ok: true; count: number }> =>
    request('/admin/products/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),

  // ── Розклад / events (admin) ──
  adminEvents: (): Promise<{ items: AdminEventDTO[] }> =>
    request('/admin/events', { method: 'GET' }),
  adminEvent: (id: string): Promise<AdminEventDTO> =>
    request(`/admin/events/${id}`, { method: 'GET' }),
  adminCreateEvent: (body: AdminEventBody): Promise<AdminEventDTO> =>
    request('/admin/events', { method: 'POST', body: JSON.stringify(body) }),
  adminPatchEvent: (id: string, body: AdminEventBody): Promise<AdminEventDTO> =>
    request(`/admin/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteEvent: (id: string): Promise<{ ok: true }> =>
    request(`/admin/events/${id}`, { method: 'DELETE' }),
  adminEventStats: (id: string, page = 1): Promise<EventStatsDTO> =>
    request(`/admin/events/${id}/stats?page=${page}`, { method: 'GET' }),

  // ── Розклад / events (user) ──
  eventsUpcoming: (): Promise<{ items: EventListItemDTO[] }> =>
    request('/events/upcoming', { method: 'GET' }),
  eventsPast: (): Promise<{ items: EventListItemDTO[] }> =>
    request('/events/past', { method: 'GET' }),
  event: (id: string): Promise<EventDetailDTO> =>
    request(`/events/${id}`, { method: 'GET' }),
  eventJoin: (id: string): Promise<{ url: string }> =>
    request(`/events/${id}/join`, { method: 'POST' }),
  eventRecordingPlayback: (id: string): Promise<PlaybackDTO> =>
    request(`/events/${id}/recording/playback`, { method: 'GET' }),
  eventRecordingProgress: (id: string): Promise<EventRecordingProgress> =>
    request(`/events/${id}/recording/progress`, { method: 'GET' }),
  eventRecordingProgressSave: (
    id: string,
    sec: number,
    duration: number,
  ): Promise<{ ok: true; maxProgressPct: number; completedAt: string | null }> =>
    request(`/events/${id}/recording/progress`, {
      method: 'POST',
      body: JSON.stringify({ sec, duration }),
    }),
  adminRematchTariffs: (): Promise<{ ok: true; count: number }> =>
    request('/admin/products/tariffs/rematch', { method: 'POST' }),
  adminBunnySync: (): Promise<{
    ok: true;
    collectionsLinked?: number;
    collectionsCreated?: number;
    collectionsImported?: number;
    total?: number;
    updated?: number;
    unchanged?: number;
    moved?: number;
    materialsImported?: number;
    orphansSkipped?: number;
    skipped?: string;
  }> => request('/admin/bunny/sync', { method: 'POST' }),
  adminBunnyPlayback: (guid: string): Promise<{ iframeUrl: string }> =>
    request(`/admin/bunny/playback/${guid}`, { method: 'GET' }),
  adminBunnyUploadCredentials: (
    title: string,
    categoryId?: string,
  ): Promise<{ videoGuid: string; libraryId: string; signature: string; expirationTime: number }> =>
    request('/admin/bunny/upload-credentials', {
      method: 'POST',
      body: JSON.stringify({ title, ...(categoryId ? { categoryId } : {}) }),
    }),
  adminCreateMaterial: (body: {
    title: string;
    categoryId: string;
    type: MaterialType;
    description?: string | null;
    bunnyVideoId?: string | null;
    durationSec?: number | null;
    isPublished?: boolean;
    sortOrder?: number;
    body?: Block[] | null;
  }): Promise<{ id: string }> =>
    request('/admin/materials', { method: 'POST', body: JSON.stringify(body) }),
  /** Кабінет рефералки. Створює код при першому виклику. */
  referralMe: (): Promise<ReferralMeDTO> => request('/referral/me'),
  referralEarnings: (cursor?: string | null): Promise<ReferralEarningsResponse> =>
    request(`/referral/earnings${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  adminReferrers: (): Promise<AdminReferrersResponse> => request('/admin/referrals'),
  adminReferralPendingReview: (): Promise<{ items: AdminPendingPaymentDTO[] }> =>
    request('/admin/referrals/pending-review'),
  adminReferralDetail: (telegramId: string): Promise<AdminReferralDetailDTO> =>
    request(`/admin/referrals/${telegramId}`),
  adminReferralAccrue: (paymentId: string): Promise<{ ok: true; amount: number }> =>
    request('/admin/referrals/accrue', { method: 'POST', body: JSON.stringify({ paymentId }) }),
  adminReferralAdjust: (
    telegramId: string,
    amount: number,
    note: string,
  ): Promise<{ ok: true; available: number; pending: number }> =>
    request(`/admin/referrals/${telegramId}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    }),
  adminGetSettings: (): Promise<AdminSettingsDTO> => request('/admin/settings'),
  /** Часткове оновлення: шлемо лише змінені поля, решта лишається як була. */
  adminUpdateReferralSettings: (patch: Partial<ReferralSettingsDTO>): Promise<AdminSettingsDTO> =>
    request('/admin/settings/referral', { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Плеєр зберігається цілим — форма завжди шле всі поля. */
  adminUpdatePlayerSettings: (settings: PlayerSettings): Promise<AdminSettingsDTO> =>
    request('/admin/settings/player', { method: 'PUT', body: JSON.stringify(settings) }),
  /** Delete a previously-uploaded image from Bunny Storage. Used by
   *  BlockEditor to keep Bunny in sync with the local editing state — admin
   *  removes an image, we wipe it immediately without waiting for save. */
  adminDeleteImage: (url: string): Promise<{ ok: true }> =>
    request('/admin/images/delete', { method: 'POST', body: JSON.stringify({ url }) }),
  /**
   * Upload a single image to Bunny Storage via the API. Sends the raw bytes
   * with Content-Type matching the file's MIME — no FormData involved. Backend
   * returns the public CDN URL ready to store in coverImage / push into mediaGroup.
   */
  adminUploadImage: async (file: File): Promise<{ url: string }> => {
    const res = await fetch(`${BASE}/admin/images/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!res.ok) {
      let code = 'unknown';
      try {
        const body = (await res.json()) as { error?: string };
        code = body.error ?? `http_${res.status}`;
      } catch {
        code = `http_${res.status}`;
      }
      throw new ApiError(res.status, code);
    }
    return (await res.json()) as { url: string };
  },
};
